/* eslint-disable sonarjs/no-duplicate-string */
import {
  Badge,
  Box,
  Button,
  HStack,
  Icon,
  Stack,
  Text,
  useColorMode,
  VStack,
} from "@chakra-ui/react";
import NextLink from "next/link";
import { useEffect, useState } from "react";
import { FiArrowRight } from "react-icons/fi";

/**
 * Section 4a — "Pricing page must display the offer conditionally, not just
 * apply it silently at checkout." Reads from the same attribution resolution
 * logic used at actual checkout (`/api/attribution/offer` -> `subscribe.tsx`),
 * so what's shown here and what the visitor is actually charged always agree.
 */
const useConversionOffer = () => {
  const [hasOffer, setHasOffer] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/attribution/offer")
      .then((response) => response.json())
      .then((data: { offerCouponId: string | null }) => {
        if (!cancelled) setHasOffer(Boolean(data.offerCouponId));
      })
      .catch(() => {
        // Best-effort — fall back to standard pricing display on any error.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return hasOffer;
};

const PricingDetails = () => {
  const { colorMode } = useColorMode();
  const hasConversionOffer = useConversionOffer();

  return (
    <Stack
      p={{ base: 8, md: 12 }}
      justify="center"
      align="center"
      bg={colorMode === "light" ? "neutral.50" : "rgba(255, 255, 255, 0.02)"}
      minW={{ lg: "320px" }}
      spacing={6}
    >
      {hasConversionOffer && (
        <Badge
          bg="rgba(255, 79, 0, 0.12)"
          color="brand.500"
          px={4}
          py={2}
          borderRadius="full"
          fontSize="sm"
          fontWeight={700}
          textAlign="center"
        >
          Special offer available at checkout — $1 for your first 14 days
        </Badge>
      )}

      <Text
        fontSize="lg"
        fontWeight={600}
        textAlign="center"
        color={
          colorMode === "light" ? "text.light-secondary" : "text.dark-secondary"
        }
      >
        Choose your billing
      </Text>

      {/* Monthly Price */}
      <VStack spacing={1}>
        <HStack align="baseline" spacing={1}>
          <Text
            fontSize="5xl"
            fontWeight={800}
            color={
              colorMode === "light" ? "text.light-primary" : "text.dark-primary"
            }
          >
            $8
          </Text>
          <Text
            fontSize="xl"
            fontWeight={500}
            color={
              colorMode === "light" ? "text.light-muted" : "text.dark-muted"
            }
          >
            / month
          </Text>
        </HStack>
      </VStack>

      <HStack spacing={4}>
        <Box
          w="40px"
          h="1px"
          bg={colorMode === "light" ? "neutral.100" : "neutral.800"}
        />
        <Text
          color={colorMode === "light" ? "text.light-muted" : "text.dark-muted"}
          fontWeight={600}
          fontSize="sm"
        >
          OR
        </Text>
        <Box
          w="40px"
          h="1px"
          bg={colorMode === "light" ? "neutral.100" : "neutral.800"}
        />
      </HStack>

      {/* Yearly Price */}
      <VStack spacing={2}>
        <HStack align="baseline" spacing={1}>
          <Text
            fontSize="5xl"
            fontWeight={800}
            color={
              colorMode === "light" ? "text.light-primary" : "text.dark-primary"
            }
          >
            $70
          </Text>
          <Text
            fontSize="xl"
            fontWeight={500}
            color={
              colorMode === "light" ? "text.light-muted" : "text.dark-muted"
            }
          >
            / year
          </Text>
        </HStack>
        <Badge
          bg="rgba(255, 79, 0, 0.1)"
          color="brand.500"
          px={4}
          py={1}
          borderRadius="full"
          fontSize="sm"
          fontWeight={600}
        >
          Save ~27% yearly
        </Badge>
      </VStack>

      <Button
        as={NextLink}
        href="/account/subscription"
        w="full"
        size="lg"
        bg="brand.500"
        color="white"
        px={8}
        fontSize="md"
        fontWeight={600}
        borderRadius="xl"
        rightIcon={<Icon as={FiArrowRight} aria-hidden="true" />}
        _hover={{
          bg: "brand.600",
          transform: "translateY(-1px)",
          textDecoration: "none",
        }}
        transition="transform 0.2s ease, background-color 0.2s ease"
      >
        Get Started
      </Button>

      <Text
        fontSize="sm"
        color={colorMode === "light" ? "text.light-muted" : "text.dark-muted"}
        textAlign="center"
      >
        14-day money-back guarantee
      </Text>
    </Stack>
  );
};

export default PricingDetails;
